/* global google */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import styled from 'styled-components';
import { withRouteData } from 'react-static';

import BlockTitle from '../global/typography/BlockTitle';
import CustomMap from './CustomMap';
import InnerWrapper from '../global/InnerWrapper';
import P from '../global/typography/P';
import Page from '../global/Page';
import Results from './Results';
import SearchForm from './SearchForm';

import { getResults, transformResults } from './utils';

const FlexWrapper = styled.div`
  display: flex;
  @media ${p => p.theme.media.largePhone} {
    flex-direction: column;
  }
`;

const FormWrapper = styled.div`
  margin: 0 40px 0 0;
  width: 40%;
  @media ${p => p.theme.media.largePhone} {
    width: 100%;
    margin: 0 0 40px 0;
  }
`;

const MapWrapper = styled.div`
  width: 60%;
  @media ${p => p.theme.media.largePhone} {
    height: 200px;
    width: 100%;
  }
`;

const ResultsTitle = styled(BlockTitle)`
  border: none;
  @media (max-width: ${props => props.theme.deviceWidth.mobilePhone}) {
    font-size: 22px;
  }
`;

const Wrapper = styled(InnerWrapper)`
  margin: 100px auto;
`;

const BulletLabel = styled.span`
  font-style: italic;
  font-weight: ${props => props.theme.fontWeight.bold};
`;

const UL = styled(P).attrs({ as: 'ul' })`
  margin-bottom: 20px;
  padding-bottom: 20px;
  padding-top: 10px;
  border-bottom: solid 1px ${props => props.theme.color.borderPrimary};
`;

const LoadingOverlay = styled.div`
  position: fixed;
  width: 100%;
  height: 100%;
  inset: 0;
  background-color: rgba(255, 255, 255, 0.8);
  z-index: 2;
  cursor: pointer;
`;

const LoadingWrapper = styled.div`
  width: 200px;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;

  img {
    width: 70px;
    height: 49px;
    margin: auto;
  }

  p {
    color: ${props => props.theme.color.textPrimary};
    font-size: 12px;
    font-family: ${props => props.theme.font.sourceSans};
    font-weight: ${props => props.theme.fontWeight.bold};
    margin: auto;
    margin-top: 10px;
    text-transform: uppercase;
  }
`;

const LoadingSpinner = styled.span`
  height: 4px;
  width: 141px;
  display: inline-block;
  margin: auto;
  margin-top: 10px;
  position: relative;
  background-color: ${props => props.theme.color.borderPrimaryOverlay};
  overflow: hidden;

  /* the moving part */
  &::after {
    content: '';
    width: 20%;
    height: 4px;
    background: ${props => props.theme.color.primary};
    position: absolute;
    top: 0;
    left: 0;
    box-sizing: border-box;
    animation: animloader 1.25s cubic-bezier(0.32, 0, 0.67, 0) infinite;
  }

  @keyframes animloader {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(500%);
    }
  }
`;

class WhereToBuy extends Component {
  state = {
    isLoading: false,
    repType: null,
    results: [],
    resultsHeading: 'Results',
    search: false
  };

  map = null;

  setMapInstance = map => {
    this.map = map;
  };

  resetResults = () => this.setState({ results: [], search: false });

  runSearch = async (formData, heading) => {
    this.setState({
      ...this.state,
      isLoading: true,
      search: false
    });
    let results = await getResults(formData);
    results = transformResults(results);
    this.setState({
      isLoading: false,
      repType: heading,
      results,
      resultsHeading: `${heading || 'Result'}s ( ${results.length} )`,
      search: true
    });

    if (results.length > 0) {
      const bounds = new google.maps.LatLngBounds();
      results.filter(r => r.location).forEach(r => bounds.extend(r.location));
      this.map.fitBounds(bounds);
    }
  };

  showInfoWindow = result => {
    const { id } = result;
    let { results } = this.state;

    results = results.map(r => {
      if (r.id === id && !r.show) {
        return { ...r, show: true };
      }

      return { ...r, show: false };
    });

    this.setState({ results });
  };

  render() {
    return (
      <Page>
        <Helmet
          meta={[
            { property: 'og:title', content: 'Dealer and Service Locator' },
            {
              property: 'description',
              content:
                "Find the nearest sales rep, contractor, or distributor with our handy locator tool. We'll tell you who to contact and where to find them!'"
            }
          ]}
          title="Where To Buy Lochinvar Products"
        />
        <Wrapper>
          <BlockTitle>Locator</BlockTitle>
          <FlexWrapper>
            <FormWrapper>
              <P>
                This is your single-source solution for who to contact and where
                to buy any of our products.
              </P>
              <UL>
                <li>
                  <BulletLabel>Service Contractor</BulletLabel>: Who to Call for
                  Service
                </li>
                <li>
                  <BulletLabel>Distributor</BulletLabel>: Where to Find
                  Lochinvar Product
                </li>
                <li>
                  <BulletLabel>Factory Rep</BulletLabel>: Who to Call for Sales
                </li>
              </UL>
              <SearchForm
                internationalLocations={this.props.internationalLocations}
                onSearch={this.runSearch}
                onSearchTypeChange={this.resetResults}
                roleOptions={this.props.roleOptions}
                training={this.props.training}
              />
            </FormWrapper>
            <MapWrapper>
              <CustomMap
                containerElement={<div style={{ height: '100%' }} />}
                googleMapURL="https://maps.googleapis.com/maps/api/js?key=AIzaSyCiAd5-Ewg3djfTlPN0MCJ0GyI8IV_rb_s"
                loadingElement={<div style={{ height: `100%` }} />}
                mapElement={<div style={{ height: '100%' }} />}
                markers={this.state.results}
                onMapLoad={this.setMapInstance}
                onMarkerClick={this.showInfoWindow}
              />
            </MapWrapper>
          </FlexWrapper>
          {this.state.isLoading && (
            <LoadingOverlay>
              <LoadingWrapper>
                <img
                  src="/images/lochinvar-knight.png"
                  alt="Lochinvar Kight Crest"
                />
                <p>Loading Search Results</p>
                <LoadingSpinner />
              </LoadingWrapper>
            </LoadingOverlay>
          )}
          {this.state.search && (
            <div>
              <ResultsTitle topMargin>{this.state.resultsHeading}</ResultsTitle>
              <Results
                results={this.state.results}
                showAvatar
                showTraining={this.state.repType === 'Service Contractor'}
              />
            </div>
          )}
        </Wrapper>
      </Page>
    );
  }
}

WhereToBuy.propTypes = {
  internationalLocations: PropTypes.array,
  roleOptions: PropTypes.array,
  training: PropTypes.array
};

export default withRouteData(WhereToBuy);
